"use client";

import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Package, Ship, Truck, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface RoutePoint {
  x: number;
  y: number;
  label: string;
  color: string;
}
interface Route {
  from: {
    x: number;
    y: number;
  };
  to: {
    x: number;
    y: number;
  };
  color: string;
  progress: number;
}
const routePoints: RoutePoint[] = [{
  x: 15,
  y: 45,
  label: "Dubai",
  color: "hsl(65 70% 50%)"
}, {
  x: 35,
  y: 30,
  label: "Singapore",
  color: "hsl(65 70% 50%)"
}, {
  x: 55,
  y: 50,
  label: "Lagos",
  color: "hsl(65 70% 50%)"
}, {
  x: 75,
  y: 35,
  label: "Mumbai",
  color: "hsl(65 70% 50%)"
}, {
  x: 85,
  y: 60,
  label: "Berlin",
  color: "hsl(65 70% 50%)"
}];
const routes: {
  from: number;
  to: number;
}[] = [{
  from: 0,
  to: 1
}, {
  from: 1,
  to: 2
}, {
  from: 1,
  to: 3
}, {
  from: 3,
  to: 4
}];
const capabilities = [{
  icon: Ship,
  title: "Global Freight Network",
  description: "Sea, air, and land freight coordination across 50+ countries with verified carrier partnerships.",
  route: 0
}, {
  icon: Package,
  title: "End-to-End Tracking",
  description: "Real-time shipment visibility from origin warehouse to final destination with full documentation.",
  route: 1
}, {
  icon: Truck,
  title: "Customs & Compliance",
  description: "Expert handling of documentation, duties, and regulatory requirements for seamless cross-border trade.",
  route: 2
}, {
  icon: MapPin,
  title: "Multi-Modal Solutions",
  description: "Flexible Incoterms (EXW, FOB, CIF, DDP) tailored to your supply chain needs and budget.",
  route: 3
}];
export default function LogisticsShipping() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredRoute, setHoveredRoute] = useState<number | null>(null);
  const [routeProgress, setRouteProgress] = useState<Route[]>([]);
  const animationRef = useRef<number>();
  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const updateCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };
    updateCanvasSize();
    const initialRoutes: Route[] = routes.map(({
      from,
      to
    }) => ({
      from: {
        x: routePoints[from].x / 100 * canvas.offsetWidth,
        y: routePoints[from].y / 100 * canvas.offsetHeight
      },
      to: {
        x: routePoints[to].x / 100 * canvas.offsetWidth,
        y: routePoints[to].y / 100 * canvas.offsetHeight
      },
      color: routePoints[from].color,
      progress: 0
    }));
    setRouteProgress(initialRoutes);
    let startTime = Date.now();
    const duration = 2000;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const updatedRoutes = initialRoutes.map(route => ({
        ...route,
        progress: progress
      }));
      setRouteProgress(updatedRoutes);
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      updatedRoutes.forEach((route, index) => {
        const isHovered = hoveredRoute === index;
        const lineWidth = isHovered ? 3 : 2;
        const opacity = isHovered ? 1 : 0.7;
        const currentX = route.from.x + (route.to.x - route.from.x) * route.progress;
        const currentY = route.from.y + (route.to.y - route.from.y) * route.progress;
        ctx.beginPath();
        ctx.moveTo(route.from.x, route.from.y);
        ctx.lineTo(currentX, currentY);
        ctx.strokeStyle = route.color.replace(")", ` / ${opacity})`);
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";
        ctx.stroke();
        if (route.progress > 0) {
          ctx.beginPath();
          ctx.arc(currentX, currentY, 4, 0, Math.PI * 2);
          ctx.fillStyle = route.color;
          ctx.fill();
        }
      });
      routePoints.forEach(point => {
        const x = point.x / 100 * canvas.offsetWidth;
        const y = point.y / 100 * canvas.offsetHeight;
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.fillStyle = point.color;
        ctx.fill();
        ctx.strokeStyle = "hsl(215 70% 12%)";
        ctx.lineWidth = 2;
        ctx.stroke();
      });
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    animate();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, hoveredRoute]);
  return <section id="logistics-shipping" ref={sectionRef} className="dark bg-background text-foreground relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542426/img/shipping_terminal_background.png)] bg-cover bg-center opacity-10" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className={`mb-12 text-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-4">
            Logistics & Shipping
          </h2>
          <p className="text-muted-foreground text-lg sm:text-xl max-w-3xl mx-auto">
            Seamless global coordination from origin to destination with full transparency and compliance.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <Card className="bg-primary/60 backdrop-blur-sm border-border-strong overflow-hidden">
              <CardContent className="p-0">
                <div className="relative aspect-[4/3] w-full">
                  <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-label="Interactive shipping routes map" />
                </div>
              </CardContent>
            </Card>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {routePoints.map((point, i) => <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <div data-index={i} className="flex items-center gap-2 px-3 py-2 rounded-sm bg-secondary/40 backdrop-blur-sm border border-border cursor-pointer hover:bg-secondary/60 transition-colors duration-[var(--duration-fast)]">
                      <div className="w-3 h-3 rounded-full" style={{
                    backgroundColor: point.color
                  }} aria-hidden="true" />
                      <span className="text-sm font-medium">{point.label}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Major hub: {point.label}</p>
                  </TooltipContent>
                </Tooltip>)}
            </div>
          </div>

          <div className="space-y-6">
            {capabilities.map((capability, i) => {
            const Icon = capability.icon;
            return <div key={i} data-index={i} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`} style={{
              transitionDelay: `${200 + i * 100}ms`
            }} onMouseEnter={() => setHoveredRoute(capability.route)} onMouseLeave={() => setHoveredRoute(null)}>
                  <Card className="bg-secondary/40 backdrop-blur-sm border-border hover:bg-secondary/60 hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-accent/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-accent" aria-hidden="true" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-display text-xl font-semibold mb-2">
                            {capability.title}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {capability.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}